<template>
<div class="main list-view">

	<div class="sub-header">
		<div class="input-with-control">
			<input type="text" class="search" placeholder="Search" v-model="filters.search_term" @keyup.enter="getList()" v-focus>
			<div class="button action input-go" @click="getList()"><i class="fas fa-search"></i></div>
		</div>
		<div class="controls">
			<pagination v-if="showPagination" :paging="paging" @updatePage="goToPage"></pagination>
		</div>
	</div>

	<div class="filters">

		<div v-for="(distributor, index) in distributors" :class="['filter-list-button-large', { active: distributorId == distributor.distributor_id }]" @click="getDistributor(distributor.distributor_id)">{{ distributor.label }}</div>

		<div class="divider"></div>
		<label>Department</label>
		<select v-model="filters.department_id" @change="getList()">
			<option value="">Select...</option>
			<option v-for="(department, index) in departments" :value="department.department_id">{{ department.description }}</option>
		</select>
		<ul class="checkbox-group">
			<li>
				<span :class="['pointer', 'far', filters.in_stock ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('in_stock')"></span>
				<label>In Stock Only</label>
			</li>
		</ul>
		<div :class="['button', list.length > 0 && !showReviewOrder ? 'action' : 'disabled']" @click="reviewOrder()">Review Order</div>

		<div v-if="hasFilters" :class="['button', list.length > 0 && !saving ? 'neutral' : 'disabled' ]" @click="removeFiltered()">Remove Filtered</div>
		<div v-else-if="hasSelected" :class="['button', list.length > 0 && !saving ? 'neutral' : 'disabled' ]" @click="removeSelected()">Remove Selected</div>
		<div v-else :class="['button', list.length > 0 && !saving ? 'delete' : 'disabled' ]" @click="removeAll()">Empty Cart</div>

		<template v-if="storeAdministrator && managerCarts.length > 1">
			<div class="divider"></div>
			<label>Manager Carts</label>
			<select v-model="filters.cart_user_id" @change="getList()">
				<option value="">Select...</option>
				<option v-for="(cart, index) in managerCarts" :value="cart.user_id">{{ cart.user_name }} ({{ cart.cart_count }})</option>
			</select>
			<div :class="['button', saving ? 'disabled' : 'neutral']" @click="confirmWhich = 'merge'; showConfirm = true">Merge All Carts</div>
			<div :class="['button', saving ? 'disabled' : 'neutral']" @click="confirmWhich = 'empty'; showConfirm = true">Empty All Carts</div>
		</template>

	</div>

    <loading v-if="loading"></loading>

	<div v-else-if="showReviewOrder" class="list review-order">
		<div class="three-columns">
			<div class="review-order-section">
				<h2>Distributor</h2>
				<div>{{ orderDistributor.company_name }}</div>
				<div>{{ orderDistributor.address_1 }}</div>
				<div v-if="orderDistributor.address_2">{{ orderDistributor.address_2 }}</div>
				<div>{{ orderDistributor.city }}, {{ orderDistributor.state }} {{ orderDistributor.zip_code }}</div>
			</div>
			<div class="review-order-section">
				<h2>Ship To</h2>
				<div>{{ orderShipTo.company_name }}</div>
				<div>{{ orderShipTo.address_1 }}</div>
				<div v-if="orderShipTo.address_2">{{ orderShipTo.address_2 }}</div>
				<div style="margin: 0 0 0.5em 0;">{{ orderShipTo.city }}, {{ orderShipTo.state }} {{ orderShipTo.zip_code }}</div>
				<div class="button delete-large smaller" @click="removeFromCart(item.product_id)" title="Edit ship to"><i class="fas fa-edit"></i></div>
			</div>
			<div class="review-order-section">
				<h2>Summary</h2>
				<table>
					<tr><td class="label">Total Cost</td><td class="right">${{ orderTotal('cost') }}</td></tr>
					<tr><td class="label">Total Retail</td><td class="right">${{ orderTotal('price') }}</td></tr>
					<tr><td class="label">Total Margin</td><td class="right">${{ orderTotal('margin') }}</td></tr>
				</table>
			</div>
		</div>
		<table class="list order-data-grid">
			<thead>
				<tr>
					<th>Item</th><th>Item Number</th><th class="right">Cost</th><th class="right">Qty</th><th class="right">Extended Cost</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="(item, index) in list">
					<td>{{ item.description }}</td>
					<td>{{ item.item_number }}</td>
					<td class="right">${{ item.cost }}</td>
					<td class="right">{{ item.quantity }}</td>
					<td class="right">${{ (item.quantity * item.cost).toFixed(2) }}</td>
				</tr>
				<tr><td colspan="4" class="right"><b>Order Total</b></td><td class="right"><b>${{ orderTotal('cost') }}</b></td></tr>
			</tbody>
		</table>

		<div class="two-columns controls">
			<div>
				<div>
					<label style="margin: 0 0.5em 0 0;">Total Cost</label>${{ orderTotal('cost') }}
					<label style="margin: 0 0.5em 0 1em;">Total Retail</label>${{ orderTotal('price') }}
					<label style="margin: 0 0.5em 0 1em;">Total Margin</label>${{ orderTotal('margin') }}
				</div>
 				<div class="note">NOTE: margin does not include distributor shipping charges.</div>
			</div>
			<div style="text-align: right;">
				<div class="button cancel" @click="showReviewOrder = false">Cancel</div>
				<div class="button action" @click="startPlaceOrder()">Place Order</div>
			</div>
		</div>

	</div>

	<div v-else class="list">

	    <div v-if="list.length == 0" class="empty-list">
			<span v-if="currentUserCartCount > 0">There are {{ currentUserCartCount }} items in the reorder cart,<br>but none are carried by this distributor.</span>
			<span v-else>This reorder cart is empty</span>
	    </div>

		<div v-else v-for="(item, index) in list" class="cart-data-grid">
			<div class="cart-data-grid-heading">
	            <label class="checkbox with-outside-label item-checkbox">
	                <input type="checkbox" v-model="item.selected">
	                <span><i class="fas fa-check-circle"></i></span>
	            </label>
				<div class="title" @click="getDetail(item.product_id)">{{ item.description }}</div>
			</div>
			<div class="cart-data-image">
				<img :src="item.image_src" @click="getDetail(item.product_id)">
			</div>
			<div>
				<table class="data-table">
					<tr><td class="label right">ID</td><td class="data">{{ item.product_id }}</td><td class="label right">Cost</td><td class="data">${{ item.cost }}</td></tr>
					<tr><td class="label right">UPC</td><td class="data">{{ item.upc }}</td><td class="label right">Retail</td><td class="data">${{ item.price }}</td></tr>
					<tr><td class="label right">Item</td><td class="data">{{ item.item_number }}</td><td class="label right">Margin</td><td class="data">${{ item.margin }}</td></tr>
				</table>
			</div>
			<div class="meta">
				<table class="data-table">
					<tr><td class="label right">30 Day Sales</td><td class="data">{{ item.sales }}</td></tr>
					<tr><td class="label right">On Hand</td><td class="data">{{ item.on_hand }}</td></tr>
					<tr><td class="label right">On Order</td><td class="data">{{ item.on_order }}</td></tr>
				</table>
			</div>
			<div class="meta">
				<table class="data-table">
					<tr><td class="label right">Reorder Point</td><td class="data">{{ item.reorder_point }}</td></tr>
					<tr><td class="label right">Restock Qty</td><td class="data">{{ item.order_quantity }}</td></tr>
					<tr><td class="label right"></td><td class="data"><div class="button neutral smaller" title="Manage reorder points" @click="setReorderPoints(item)"><i class="fas fa-edit"></i></div></td></tr>
				</table>
			</div>
			<div class="meta">
				<table class="data-table">
					<tr><td class="label right">Available</td><td class="data">{{ item.available }}</td></tr>
					<tr>
						<td class="label right">Order Qty</td>
						<td class="data">
							<input type="number" :disabled="item.available < 1" v-model="item.quantity">
							<div class="button delete-large smaller" @click="removeFromCart(item.product_id)" title="Remove from cart"><i class="remove-from-cart fas fa-trash-alt"></i></div>
						</td>
					</tr>
				</table>
			</div>
		</div>

	</div>

	<div class="modal" v-if="showDetail">
		<div class="inner">
			<h2>Product Detail</h2>
			<div class="content">
				<table class="data-table">
					<tr><td class="label right">UPC</td><td class="data">{{ productDetail.upc }}</td></tr>
					<tr><td class="label right">Description</td><td class="data">{{ productDetail.description }}</td></tr>
					<tr><td class="label right">Department</td><td class="data">{{ productDetail.department }}</td></tr>
					<tr><td class="label right">Cateogry</td><td class="data">{{ productDetail.category }}</td></tr>
					<tr><td class="label right">Caliber</td><td class="data">{{ productDetail.caliber }}</td></tr>
					<tr><td class="label right">Manufacturer</td><td class="data">{{ productDetail.manufacturer }}</td></tr>
					<tr><td class="label right">Model</td><td class="data">{{ productDetail.model }}</td></tr>
					<tr><td class="label right">SKU</td><td class="data">{{ productDetail.sku }}</td></tr>
				</table>
			</div>
			<div class="controls">
				<div class="button cancel" @click="showDetail = false">Back To List</div>
			</div>
		</div>
	</div>

	<div class="modal" v-if="showOrderPoints">
		<div class="inner">
			<h2>Order Points</h2>
			<div class="content">
				<div class="title"><b>{{ orderPoints.description }}</b></div>
				<p class="center">UPC {{ orderPoints.upc }}</p>
				<table class="data-table">
					<tr><td class="label right">Reorder Point</td><td class="data"><input type="number" min="0" v-model="orderPoints.reorder_point"></td></tr>
					<tr><td class="label right">Restock Quantity</td><td class="data"><input type="number" min="0" v-model="orderPoints.order_quantity"></td></tr>
				</table>
			</div>
			<div class="controls">
				<div class="button cancel" @click="showOrderPoints = false">Cancel</div>
				<div class="button action" @click="saveOrderPoints()">Save Order Points</div>
			</div>
		</div>
	</div>

	<div class="modal" v-if="showConfirm">
		<div class="inner">
			<h2>{{ confirmWhich }} All Carts</h2>
			<div class="content">
				<p>
					<table>
						<tr v-for="(cart, index) in managerCarts"><td>User: <b>{{ cart.user_name }}</b></td><td style="padding: 0 0 0 1em;">Items in cart: <b>{{ cart.cart_count }}</b></td></tr>
					</table>
				</p>
				<p v-if="confirmWhich == 'merge'" class="center">
					Are you sure you want to merge all reorder carts into this cart?<br>
					Other carts will be emptied and this cart will contain all items.
				</p>
				<p v-if="confirmWhich == 'empty'" class="center">
					Are you sure you want to empty all reorder carts?
				</p>
				<p v-if="message" class="note">{{ message }}</p>
			</div>
			<div class="controls">
				<div class="button cancel" @click="showConfirm = false">Cancel</div>
				<div v-if="confirmWhich == 'merge'" class="button action" @click="mergeAllCarts()">Yes, Merge</div>
				<div v-if="confirmWhich == 'empty'" class="button action" @click="emptyAllCarts()">Yes, Empty</div>
			</div>
		</div>
	</div>

	<div class="modal confirm-order" v-if="confirmPlaceOrder">
		<div class="inner">
			<h2>{{ placeOrderHeading }}</h2>
			<div class="content">
				<p class="center" v-html="placeOrderMessage"></p>
				<p v-if="saving" class="center" style="font-size: 1.5em;"><i class="fa fa-spinner fa-spin"></i></p>
			</div>
			<div class="controls">
				<div class="button cancel" @click="confirmPlaceOrder = false">Cancel</div>
				<div :class="['button', saving ? 'disabled' : 'action']" @click="placeOrder()">Yes, place order</div>
			</div>
		</div>
	</div>

</div>
</template>

<script>
import Loading from '@/components/Loading.vue'
export default {
	name: 'reorder-cart',
	props: ['distributor_id'],
    components: {
        'loading': Loading
    },
	data() {
		return {
            loading: false,
			storeAdministrator: false,
			showPagination: false,
			clientId: '',
			filters: {
				page: 1,
				search_term: '',
				distributor_id: '',
				department_id: '',
				cart_user_id: '',
				in_stock: false
			},
			distributors: [],
			distributorId: '',
			departments: [],
			managerCarts: [],
			list: [],
			paging: {},
			showDetail: false,
			productDetail: {},
			showOrderPoints: false,
			orderPoints: {},
			showReviewOrder: false,
			orderDistributor: {},
			orderShipTo: {},
			showConfirm: false,
			confirmWhich: '',
			confirmPlaceOrder: false,
			placeOrderHeading: '',
			placeOrderMessage: '',
			saving: false
		}
	},
    created() {
		this.getDistributors()
		this.getDepartments()
    },
	computed: {
		hasSelected: function() {
			for (let i = 0; i < this.list.length; i++) {
				if (this.list[i]['selected']) {
					return true
				}
			}
			return false
		},
		hasFilters: function() {
			return this.filters.search_term || this.filters.department_id || this.filters.in_stock
		},
		currentUserCartCount() {
			for (let i = 0; i < this.managerCarts.length; i++) {
				if (this.managerCarts[i]['user_id'] == this.filters.cart_user_id) {
					return this.managerCarts[i]['cart_count']
				}
			}
			return 0
		}
	},
	methods: {
		getDistributors() {
			this.apiCall({ destination: 'get_list_client_distributors_for_inventory' }).then(obj => {
				this.distributors = obj.distributors
				this.getDistributor(this.distributors[0]['distributor_id'])
				this.storeAdministrator = obj.user.administrator_flag
				if (this.storeAdministrator) {
					this.getManagerCarts()
				}
			}).catch(err => {
				console.log(err)
			})
		},
		getDepartments() {
			this.apiCall({ destination: 'get_list_departments' }).then(obj => {
				this.departments = obj.departments
			}).catch(err => {
				console.log(err)
			})
		},
		getManagerCarts() {
			this.apiCall({ destination: 'get_list_manager_carts' }).then(obj => {
				this.managerCarts = obj.manager_carts
			}).catch(err => {
				console.log(err)
			})
		},
		getDistributor(distributorId) {
			if (distributorId != this.distributorId) {
				this.distributorId = distributorId
				this.getList()
			}
		},
		toggleFilter(which) {
			this.filters[which] = !this.filters[which]
			this.getList()
		},
		getList() {
			if (this.loading) { return }
            this.loading = true
			this.showDetail = false
			this.filters.distributor_id = this.distributorId
			this.apiCall({ destination: 'get_list_reorder_cart', data: this.filters }).then(obj => {
                this.loading = false
				this.list = obj.list
				this.paging = obj.paging
				this.clientId = obj.client_id
				this.showPagination = true
				this.filters.cart_user_id = obj.cart_user_id
			}).catch(err => {
                this.loading = false
				console.log(err)
			})
		},
		removeFromCart(which) {
			if (this.saving) { return }
			this.saving = true
			this.message = ''
			let productIds = []
			if (Array.isArray(which)) {
				productIds = which
			} else {
				productIds.push(which)
			}
			this.apiCall({ destination: 'remove_from_manager_carts', data: { cart_user_id: this.filters.cart_user_id, product_ids: productIds } }).then(obj => {
				this.saving = false
				this.message = obj.message
				if (obj.status == 'success') {
					this.getList()
					this.getManagerCarts()
				}
			}).catch(err => {
				console.log(err)
			})
		},
		removeSelected() {
			if (!this.hasSelected) { return }
			let productIds = []
			for (let i = 0; i < this.list.length; i++) {
				if (this.list[i]['selected']) {
					productIds.push(this.list[i]['product_id'])
				}
			}
			this.removeFromCart(productIds)
		},
		removeFiltered() {
			if (this.list.length < 1) { return }
			let productIds = []
			for (let i = 0; i < this.list.length; i++) {
				productIds.push(this.list[i]['product_id'])
			}
			this.removeFromCart(productIds)
		},
		removeAll() {
			if (this.saving) { return }
			this.saving = true
			this.message = ''
			this.apiCall({ destination: 'remove_from_manager_carts', data: { cart_user_id: this.filters.cart_user_id, product_ids: 'all' } }).then(obj => {
				this.saving = false
				this.message = obj.message
				if (obj.status == 'success') {
					this.getList()
					this.getManagerCarts()
				}
			}).catch(err => {
				console.log(err)
			})
		},
		reviewOrder() {
			if (this.list.length == 0) { return }
			if (this.showReviewOrder) { return }
			// get distributor details, then show review section
			this.apiCall({ destination: 'get_reorder_contact_info', data: { distributor_id: this.distributorId } }).then(obj => {
				this.orderDistributor = obj.order_distributor
				this.orderShipTo = obj.order_ship_to
				this.showReviewOrder = true
			}).catch(err => {
				console.log(err)
			})
		},
		orderTotal(column) {
			let total = 0
			for (let i = 0; i < this.list.length; i++) {
				total += parseFloat(this.list[i][column]) * parseFloat(this.list[i]['quantity'])
			}
			return total.toFixed(2)
		},
		getDetail(productId) {
			this.apiCall({ destination: 'get_item_product_by_id', data: { product_id: productId } }).then(obj => {
				if (obj.status == 'success') {
					this.productDetail = obj.product_detail
					this.showDetail = true
				}
			}).catch(err => {
				console.log(err)
			})
		},
		setReorderPoints(item) {
			this.orderPoints = item
			this.showOrderPoints = true
		},
		saveOrderPoints() {
			if (this.saving) { return }
			this.saving = true
			this.message = ''
			this.apiCall({ destination: 'save_item_product_reorder_actions', data: { product_id: this.orderPoints.product_id, reorder_point: this.orderPoints.reorder_point, order_quantity: this.orderPoints.order_quantity } }).then(obj => {
				this.saving = true
				this.message = obj.message
				if (obj.status == 'success') {
					this.showOrderPoints = false
				}
			}).catch(err => {
				console.log(err)
			})
		},
		mergeAllCarts() {
			if (this.saving) { return }
			this.saving = true
			this.message = ''
			this.apiCall({ destination: 'merge_manager_carts', data: { cart_user_id: this.filters.cart_user_id } }).then(obj => {
				this.saving = false
				this.message = obj.message
				if (obj.status == 'success') {
					this.showConfirm = false
					this.getList()
					this.getManagerCarts()
				}
			}).catch(err => {
				console.log(err)
			})
		},
		emptyAllCarts() {
			if (this.saving) { return }
			this.saving = true
			this.message = ''
			this.apiCall({ destination: 'remove_from_manager_carts', data: { cart_user_id: 'all', product_ids: 'all' } }).then(obj => {
				this.saving = false
				this.message = obj.message
				if (obj.status == 'success') {
					this.showConfirm = false
					this.getList()
					this.getManagerCarts()
				}
			}).catch(err => {
				console.log(err)
			})
		},
		backToList() {
			this.showDetail = false
			this.detail = {}
			this.showPagination = true
		},
		goToPage(page) {
			this.filters.page = page
			this.getList()
		},
		clearFilters() {
			this.filters.page = 1
			this.filters.search_term = ''
			this.filters.department_id = ''
			this.filters.in_stock = false
			this.getList()
		},
		startPlaceOrder() {
			this.placeOrderHeading = "Place Order"
			this.placeOrderMessage = "You are about to place an order with<br>" + this.orderDistributor.company_name + " for $" + this.orderTotal('cost') + "."
			this.confirmPlaceOrder = true
		},
		placeOrder() {
			if (this.saving) { return }
			this.placeOrderHeading = "Submitting Order"
			this.placeOrderMessage = "Submitting order to<br>" + this.orderDistributor.company_name + " for $" + this.orderTotal('cost') + "."
			this.saving = true
			this.message = ''
			this.apiCall({ destination: 'create_distributor_order', data: { distributor_id: this.distributorId, items: this.list } }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.placeOrderHeading = "Success"
					this.placeOrderMessage = "Order submitted!<br><br>" + obj.message
				} else {
					this.placeOrderHeading = "Error"
					this.placeOrderMessage = "Order not submitted...<br><br>" + obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		}
	}
}
</script>

<style lang="scss" scoped>
.cart-data-grid {
	margin: 1em;
	border-bottom: 1px solid #ccc;
	display: grid;
	grid-template-columns: 7em 2fr 1fr 1fr 1fr;
	grid-column-gap: 1em;
	align-items: center;
	.cart-data-grid-heading {
		grid-column: 1 / span 5;
		.title { display: inline-block; }
	}
	.cart-data-image {
		text-align: center;
		//padding: 1em 0;
		img {
			max-width: 100%;
			max-height: 4em;
			cursor: pointer;
		}
	}
	label { padding: 0 0.5em 0 0; }
	.item-checkbox {
		font-size: 0.8em;
		margin-bottom: 0.25em;
		span { padding: 0.66em 0.25em 0.66em 0.66em; }
	}
	.title {
		font-weight: bold;
		margin: 0 0 0.25em 0;
		cursor: pointer;
	}
	.available {
		text-align: center;
		font-weight: bold;
		label { display: block; font-size: 0.8em; padding: 0; font-weight: normal; }
	}
}
table.data-table {
	td.label { padding: 0 0.66em 0 0; }
	td.data { padding: 0.25em 0; }
	input { font-size: 0.8em; width: 5em; margin: 0 1em 0 0; }
}
.review-order {
	margin: 0 0 0 1em;
	.review-order-section {
		height: 9em;
		td { padding: 0.25em 0; }
	}
	.controls {
		margin: 1em 0;
		padding: 1em 0 0 1em;
		border-top: 1px solid #ccc;
		justfiy-items: auto;
	}
}
.confirm-order {
	.content {
		height: 7em;
		width: 30em;
	}
}
</style>
